// src/components/pipelines/PipelineDirectory.js
// Pipeline Directory — With Bi-Directional Highlighting

import React, { useState, useMemo, useCallback, useRef, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Flex, HStack, VStack, Text, Heading, Badge, Icon, IconButton, Button,
  Input, InputGroup, InputLeftElement, Select, Tooltip, useColorModeValue,
  Menu, MenuButton, MenuList, MenuItem, Tag, TagLabel, SimpleGrid, Spinner,
  useToast,
} from '@chakra-ui/react';
import {
  MdSearch, MdRouter, MdEdit, MdMoreVert, MdArrowUpward, MdArrowDownward,
  MdCheckCircle, MdWarning, MdErrorOutline, MdFileDownload, MdRefresh,
  MdVisibility, MdBlock,
} from 'react-icons/md';
import { FaArrowDown } from 'react-icons/fa';

const EXCLUDED_FROM_SCANS = ['decommissioned', 'abandoned', 'removed'];

const STATUS_META = {
  active:         { label: 'Active',         scheme: 'green' },
  abandoned:      { label: 'Abandoned',      scheme: 'gray' },
  decommissioned: { label: 'Decommissioned', scheme: 'gray' },
  removed:        { label: 'Removed',        scheme: 'gray' },
};

const DEPTH_SOURCE_LABELS = {
  field_survey: 'Survey',
  as_built: 'As-built',
  estimated: 'Estimated',
};

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

const ROW_HEIGHT = 56;
const OVERSCAN = 6;

function useWindowedRows(itemCount, { rowHeight = ROW_HEIGHT, overscan = OVERSCAN, maxViewport = 560 } = {}) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(maxViewport);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => setViewportH(el.clientHeight || maxViewport))
      : null;
    if (ro) ro.observe(el);
    setViewportH(el.clientHeight || maxViewport);

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (ro) ro.disconnect();
    };
  }, [maxViewport]);

  const totalHeight = itemCount * rowHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visibleCount = Math.ceil(viewportH / rowHeight) + overscan * 2;
  const endIndex = Math.min(itemCount, startIndex + visibleCount);
  const offsetY = startIndex * rowHeight;

  return { scrollRef, startIndex, endIndex, offsetY, totalHeight, rowHeight };
}

const resolveClearance = (p) => {
  if (p.geometry_srid_ok === false) {
    return { key: 'geometry', scheme: 'red', icon: MdErrorOutline, label: 'Geometry error' };
  }
  if (EXCLUDED_FROM_SCANS.includes(p.status)) {
    return { key: 'excluded', scheme: 'gray', icon: MdBlock, label: 'Not scanned' };
  }
  if (num(p.depth_m) === null) {
    return { key: 'no_depth', scheme: 'orange', icon: MdWarning, label: 'No depth' };
  }
  return { key: 'ready', scheme: 'green', icon: MdCheckCircle, label: 'Scan-ready' };
};

// ── Single row (With Selection Highlight) ───────────────────────────────────
const PipelineRow = memo(function PipelineRow({
  pipeline, rowHeight, hoverBg, borderColor, mutedColor, isSelected, selectedBg, onSelect, onEdit,
}) {
  const p = pipeline;
  const clearance = resolveClearance(p);
  const statusMeta = STATUS_META[p.status] || { label: p.status || 'Unknown', scheme: 'gray' };
  const depth = num(p.depth_m);
  const diameter = num(p.diameter_inches);

  return (
    <Flex
      align="center"
      h={`${rowHeight}px`}
      px={4}
      borderBottomWidth="1px"
      borderColor={borderColor}
      borderLeftWidth={isSelected ? '4px' : '0px'}
      borderLeftColor="purple.400"
      bg={isSelected ? selectedBg : 'transparent'}
      _hover={{ bg: isSelected ? selectedBg : hoverBg }}
      cursor="pointer"
      onClick={() => onSelect?.(p)}
      gap={3}
      role="row"
      transition="background-color 0.15s ease"
    >
      <Box flex="2" minW="0">
        <HStack spacing={2}>
          <Icon as={MdRouter} color={isSelected ? 'purple.400' : `${clearance.scheme}.400`} boxSize={4} flexShrink={0} />
          <Box minW="0">
            <Text fontWeight="700" fontSize="sm" noOfLines={1}>{p.name || `Pipeline ${p.id}`}</Text>
            <Text fontSize="10px" color={mutedColor} noOfLines={1}>
              ID {p.id}{p.area_code ? ` · ${p.area_code}` : ''}
            </Text>
          </Box>
        </HStack>
      </Box>

      <Box flex="1" minW="0" display={{ base: 'none', md: 'block' }}>
        <Text fontSize="xs" color={mutedColor} noOfLines={1}>{p.material || '—'}</Text>
      </Box>

      <Box w="70px" display={{ base: 'none', lg: 'block' }} textAlign="right">
        {diameter !== null ? (
          <Badge
            colorScheme={diameter >= 12 ? 'red' : diameter >= 6 ? 'orange' : 'gray'}
            borderRadius="full" fontSize="10px"
          >
            {diameter}"
          </Badge>
        ) : <Text fontSize="xs" color={mutedColor}>—</Text>}
      </Box>

      <Box w="92px" textAlign="right" display={{ base: 'none', sm: 'block' }}>
        {depth !== null ? (
          <VStack spacing={0} align="flex-end">
            <Text fontSize="sm" fontWeight="700">{depth} m</Text>
            {p.depth_source && (
              <Text fontSize="9px" color={mutedColor}>{DEPTH_SOURCE_LABELS[p.depth_source] || p.depth_source}</Text>
            )}
          </VStack>
        ) : (
          <Text fontSize="xs" color="orange.400" fontWeight="600">Unsurveyed</Text>
        )}
      </Box>

      <Box w="120px" display={{ base: 'none', md: 'block' }}>
        <Tag size="sm" colorScheme={statusMeta.scheme} borderRadius="full" variant="subtle">
          <TagLabel fontSize="10px" fontWeight="700">{statusMeta.label}</TagLabel>
        </Tag>
      </Box>

      <Box w="120px">
        <Tooltip label={clearance.label} hasArrow>
          <Tag size="sm" colorScheme={clearance.scheme} borderRadius="full" variant="subtle">
            <Icon as={clearance.icon} boxSize={3} mr={1} />
            <TagLabel fontSize="10px" fontWeight="700">{clearance.label}</TagLabel>
          </Tag>
        </Tooltip>
      </Box>

      <Box w="40px" onClick={(e) => e.stopPropagation()}>
        <Menu isLazy>
          <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />} size="sm" variant="ghost" aria-label="Actions" />
          <MenuList zIndex={20}>
            <MenuItem icon={<Icon as={MdVisibility} />} onClick={() => onSelect?.(p)}>View details</MenuItem>
            {onEdit && <MenuItem icon={<Icon as={MdEdit} />} onClick={() => onEdit(p)}>Edit clearance data</MenuItem>}
            {onEdit && depth === null && (
              <MenuItem icon={<Icon as={FaArrowDown} />} color="purple.500" onClick={() => onEdit(p)}>
                Record depth
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Box>
    </Flex>
  );
});

const HeaderCell = ({ label, sortKey, activeSort, dir, onSort, ...boxProps }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const activeColor = useColorModeValue('purple.600', 'purple.300');
  const isActive = activeSort === sortKey;
  const sortable = !!sortKey;

  return (
    <Box
      {...boxProps}
      cursor={sortable ? 'pointer' : 'default'}
      onClick={sortable ? () => onSort(sortKey) : undefined}
      userSelect="none"
    >
      <HStack spacing={1}>
        <Text
          fontSize="9px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase"
          color={isActive ? activeColor : mutedColor}
        >
          {label}
        </Text>
        {isActive && <Icon as={dir === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={3} color={activeColor} />}
      </HStack>
    </Box>
  );
};

function PipelineDirectory({
  pipelines = [],
  selectedPipelineId = null, // 🚀 Highlighting prop
  isLoading = false,
  onRefresh,
  onSelectPipeline,
  onEditPipeline,
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const inputBg = useColorModeValue('white', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const tileBg = useColorModeValue('gray.50', 'gray.900');
  const selectedBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.18)');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clearanceFilter, setClearanceFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const toast = useToast();

  const handleSort = useCallback((key) => {
    setSortKey(prev => {
      if (prev === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); return prev; }
      setSortDir('asc');
      return key;
    });
  }, []);

  const counts = useMemo(() => {
    const c = { total: pipelines.length, ready: 0, noDepth: 0, excluded: 0, geometry: 0 };
    pipelines.forEach(p => {
      const k = resolveClearance(p).key;
      if (k === 'ready') c.ready += 1;
      else if (k === 'no_depth') c.noDepth += 1;
      else if (k === 'excluded') c.excluded += 1;
      else if (k === 'geometry') c.geometry += 1;
    });
    return c;
  }, [pipelines]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();

    let list = pipelines.filter(p => {
      if (q) {
        const hay = `${p.name || ''} ${p.id} ${p.material || ''} ${p.area_code || ''} ${p.pressure_zone || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (clearanceFilter !== 'all' && resolveClearance(p).key !== clearanceFilter) return false;
      return true;
    });

    const dirMul = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'diameter': av = num(a.diameter_inches) ?? -1; bv = num(b.diameter_inches) ?? -1; break;
        case 'depth':
          av = num(a.depth_m); bv = num(b.depth_m);
          if (av === null && bv === null) return 0;
          if (av === null) return -1 * dirMul;
          if (bv === null) return 1 * dirMul;
          break;
        case 'status': av = a.status || ''; bv = b.status || ''; break;
        case 'id': av = num(a.id) ?? 0; bv = num(b.id) ?? 0; break;
        case 'name':
        default: av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
      }
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });

    return list;
  }, [pipelines, search, statusFilter, clearanceFilter, sortKey, sortDir]);

  const { scrollRef, startIndex, endIndex, offsetY, totalHeight, rowHeight } = useWindowedRows(rows.length);
  const visibleRows = rows.slice(startIndex, endIndex);

  const exportCSV = useCallback(() => {
    const header = ['ID', 'Name', 'Material', 'Diameter (in)', 'Depth (m)', 'Depth Source', 'Status', 'Pressure Zone', 'Area', 'Clearance', 'Geometry SRID OK'];
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map(p => [
      p.id, p.name, p.material, p.diameter_inches, p.depth_m, p.depth_source,
      p.status, p.pressure_zone, p.area_code, resolveClearance(p).label,
      p.geometry_srid_ok === false ? 'NO' : 'yes',
    ].map(escape).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `pipeline-register-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: `${rows.length} pipelines`, status: 'success', duration: 2000, position: 'bottom-right' });
  }, [rows, toast]);

  const clearFilters = useCallback(() => {
    setSearch(''); setStatusFilter('all'); setClearanceFilter('all');
  }, []);

  const hasFilters = search || statusFilter !== 'all' || clearanceFilter !== 'all';

  if (isLoading && pipelines.length === 0) {
    return (
      <Flex justify="center" align="center" py={12}>
        <VStack spacing={3}><Spinner color="purple.500" /><Text fontSize="sm" color={mutedColor}>Loading pipeline register…</Text></VStack>
      </Flex>
    );
  }

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        {[
          { label: 'Total', value: counts.total, scheme: undefined, filter: null },
          { label: 'Scan-ready', value: counts.ready, scheme: 'green', filter: 'ready' },
          { label: 'No depth', value: counts.noDepth, scheme: 'orange', filter: 'no_depth' },
          { label: 'Not scanned', value: counts.excluded, scheme: 'gray', filter: 'excluded' },
          { label: 'Geometry error', value: counts.geometry, scheme: 'red', filter: 'geometry' },
        ].map(tile => (
          <Box
            key={tile.label}
            bg={tileBg} borderWidth="1px" borderColor={tile.filter === clearanceFilter && tile.filter ? `${tile.scheme}.400` : border}
            borderRadius="xl" p={3} cursor={tile.filter ? 'pointer' : 'default'}
            onClick={tile.filter ? () => setClearanceFilter(c => (c === tile.filter ? 'all' : tile.filter)) : undefined}
            transition="border-color 0.15s"
          >
            <Text fontSize="10px" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.06em">{tile.label}</Text>
            <Text fontSize="2xl" fontWeight="800" color={tile.scheme ? `${tile.scheme}.400` : undefined} lineHeight="1.1">{tile.value}</Text>
          </Box>
        ))}
      </SimpleGrid>

      <Flex
        justify="space-between" align="center" mb={3} gap={3} p={3}
        bg={headerBg} borderRadius="xl" borderWidth="1px" borderColor={border} flexWrap="wrap"
      >
        <HStack spacing={2} flex={1} flexWrap="wrap">
          <InputGroup size="sm" maxW="240px">
            <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input
              placeholder="Search name, material, zone…" value={search}
              onChange={e => setSearch(e.target.value)} bg={inputBg} borderRadius="lg"
            />
          </InputGroup>
          <Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} maxW="150px" bg={inputBg} borderRadius="lg">
            <option value="all">All statuses</option>
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </Select>
          <Select size="sm" value={clearanceFilter} onChange={e => setClearanceFilter(e.target.value)} maxW="160px" bg={inputBg} borderRadius="lg">
            <option value="all">All clearance</option>
            <option value="ready">Scan-ready</option>
            <option value="no_depth">No depth</option>
            <option value="excluded">Not scanned</option>
            <option value="geometry">Geometry error</option>
          </Select>
          {hasFilters && (
            <Button size="xs" variant="ghost" colorScheme="red" borderRadius="full" onClick={clearFilters}>Clear</Button>
          )}
        </HStack>

        <HStack spacing={2}>
          <Text fontSize="11px" color={mutedColor} fontWeight="600">{rows.length}/{counts.total}</Text>
          <Tooltip label="Export filtered list to CSV" hasArrow>
            <IconButton icon={<Icon as={MdFileDownload} />} size="sm" variant="ghost" colorScheme="blue" onClick={exportCSV} aria-label="Export" isDisabled={rows.length === 0} />
          </Tooltip>
          {onRefresh && (
            <Tooltip label="Reload register" hasArrow>
              <IconButton icon={<Icon as={MdRefresh} />} size="sm" variant="ghost" onClick={onRefresh} isLoading={isLoading} aria-label="Refresh" />
            </Tooltip>
          )}
        </HStack>
      </Flex>

      <Flex align="center" px={4} py={2} bg={headerBg} borderTopRadius="xl" borderWidth="1px" borderColor={border} gap={3}>
        <HeaderCell flex="2" label="Pipeline" sortKey="name" activeSort={sortKey} dir={sortDir} onSort={handleSort} />
        <HeaderCell flex="1" label="Material" display={{ base: 'none', md: 'block' }} activeSort={sortKey} dir={sortDir} onSort={handleSort} />
        <HeaderCell w="70px" label="Ø" sortKey="diameter" activeSort={sortKey} dir={sortDir} onSort={handleSort} display={{ base: 'none', lg: 'block' }} textAlign="right" />
        <HeaderCell w="92px" label="Depth" sortKey="depth" activeSort={sortKey} dir={sortDir} onSort={handleSort} display={{ base: 'none', sm: 'block' }} textAlign="right" />
        <HeaderCell w="120px" label="Status" sortKey="status" activeSort={sortKey} dir={sortDir} onSort={handleSort} display={{ base: 'none', md: 'block' }} />
        <HeaderCell w="120px" label="Clearance" activeSort={sortKey} dir={sortDir} onSort={handleSort} />
        <Box w="40px" />
      </Flex>

      {rows.length === 0 ? (
        <Box py={12} textAlign="center" borderWidth="1px" borderTopWidth="0" borderColor={border} borderBottomRadius="xl">
          <Icon as={MdSearch} boxSize={8} color={mutedColor} mb={2} />
          <Text fontSize="sm" fontWeight="600" color={mutedColor}>
            {hasFilters ? 'No pipelines match these filters' : 'No pipelines in the register'}
          </Text>
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          borderWidth="1px" borderTopWidth="0" borderColor={border} borderBottomRadius="xl"
          bg={cardBg}
          maxH="560px" overflowY="auto" position="relative"
        >
          <Box height={`${totalHeight}px`} position="relative">
            <Box position="absolute" top={0} left={0} right={0} transform={`translateY(${offsetY}px)`}>
              {visibleRows.map(p => {
                const isSelected = selectedPipelineId && String(selectedPipelineId) === String(p.id);
                return (
                  <PipelineRow
                    key={p.id}
                    pipeline={p}
                    rowHeight={rowHeight}
                    hoverBg={hoverBg}
                    borderColor={border}
                    mutedColor={mutedColor}
                    isSelected={isSelected}
                    selectedBg={selectedBg}
                    onSelect={onSelectPipeline}
                    onEdit={onEditPipeline}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {rows.length > 0 && (
        <Text fontSize="10px" color={mutedColor} mt={2} textAlign="right">
          Showing rows {startIndex + 1}–{endIndex} of {rows.length} · windowed for performance
        </Text>
      )}
    </Box>
  );
}

PipelineDirectory.propTypes = {
  pipelines: PropTypes.array,
  selectedPipelineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onSelectPipeline: PropTypes.func,
  onEditPipeline: PropTypes.func,
};

export default memo(PipelineDirectory);